import {useEffect, useState} from 'react';
import {useEventListener} from '../../infrastructure/events/useBroadcastChannel';

export const RUNNING_VERSION = {
  gitHash: process.env.REACT_APP_GIT_HASH || 'development',
  buildTime: process.env.REACT_APP_BUILD_TIME || null
};
export const NewVersionAvailableEvent = 'NEW_VERSION_AVAILABLE';
export const RequestVersionInfoEvent = 'REQUEST_VERSION_INFO';
export const VersionInfoEvent = 'VERSION_INFO';
export const UPDATE_READY_EVENT = 'app-update-ready';

export async function fetchVersion(signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort);
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 15_000);
  try {
    const response = await fetch(`/version.json?_check=${Date.now()}`, {cache: 'no-store', signal: controller.signal});
    if (!response.ok) throw new Error('Nie można sprawdzić wersji. Sprawdź połączenie i spróbuj ponownie.');
    const version = await response.json();
    if (typeof version.gitHash !== 'string' || !version.gitHash.trim()) throw new Error('Nieprawidłowa odpowiedź wersji.');
    return {gitHash: version.gitHash, buildTime: version.buildTime || null};
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Pobieranie trwa zbyt długo. Sprawdź połączenie i spróbuj ponownie.');
    throw error;
  } finally {clearTimeout(timer); signal?.removeEventListener('abort', abort);}
}

export const useVersionInfo = () => {
  const [versionInfo, setVersionInfo] = useState(RUNNING_VERSION);
  const [availableVersion, setAvailableVersion] = useState(null);
  const receive = version => {
    if (!version || typeof version.gitHash !== 'string') return;
    setAvailableVersion(version);
    if (version.gitHash === RUNNING_VERSION.gitHash) setVersionInfo(version);
  };
  useEventListener(VersionInfoEvent, receive);
  useEffect(() => {
    let active = true;
    let controller;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      controller?.abort();
      controller = new AbortController();
      try {
        const version = await fetchVersion(controller.signal);
        if (active) {
          setAvailableVersion(version);
          if (version.gitHash === RUNNING_VERSION.gitHash) setVersionInfo(version);
        }
      } catch (_) { /* Offline does not discard the last known version. */ }
    };
    check();
    const timer = setInterval(check, 60_000);
    window.addEventListener('focus', check);
    window.addEventListener(UPDATE_READY_EVENT, check);
    document.addEventListener('visibilitychange', check);
    return () => {
      active = false;
      controller?.abort();
      clearInterval(timer);
      window.removeEventListener('focus', check);
      window.removeEventListener(UPDATE_READY_EVENT, check);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);
  return {versionInfo, availableVersion,
    newVersionAvailable: !!availableVersion && availableVersion.gitHash !== versionInfo.gitHash,
    reloadForNewVersion: reloadApplication};
};

function waitForWorker(worker, ready) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {clearTimeout(timer); worker.removeEventListener('statechange', changed);};
    const changed = () => {
      if (ready.includes(worker.state)) {cleanup(); resolve();}
      else if (worker.state === 'redundant') {cleanup(); reject(new Error('Aktualizacja nie powiodła się. Spróbuj ponownie.'));}
    };
    const timer = setTimeout(() => {cleanup(); reject(new Error('Aktualizacja trwa zbyt długo. Spróbuj ponownie lub wymuś przeładowanie.'));}, 30_000);
    worker.addEventListener('statechange', changed);
    changed();
  });
}

/** Reload app assets only. Never clear song IndexedDB or personal settings. */
export async function reloadApplication(force = false) {
  await fetchVersion();
  const registration = await navigator.serviceWorker?.getRegistration();
  if (force) {
    if (registration) await registration.unregister();
    if (window.caches) {
      const names = await window.caches.keys();
      await Promise.all(names.filter(name => name.startsWith('workbox-precache-') || name === 'images')
        .map(name => window.caches.delete(name)));
    }
    const url = new URL(window.location.href);
    url.searchParams.set('_app_reload', String(Date.now()));
    window.location.replace(url.href);
    return;
  }
  if (registration) {
    await registration.update();
    if (registration.installing) await waitForWorker(registration.installing, ['installed', 'activated']);
    const waiting = registration.waiting;
    if (waiting) {
      const activated = waitForWorker(waiting, ['activated']);
      waiting.postMessage({type: 'SKIP_WAITING'});
      await activated;
    }
  }
  window.location.reload();
}
